import React from 'react';
import { Icon } from '../../../pages/DesignSystem/primitives/Icon';
import LogoSVG from '../../../pages/DesignSystem/primitives/LogoSVG';
import Button from '../../../pages/DesignSystem/primitives/Button';
import './WhyJoinUs.css';

interface WhyJoinUsContentProps {
  variant?: 'full' | 'compact';
  showCTA?: boolean;
  onCTAClick?: () => void;
  onSecondaryCTAClick?: () => void;
}

export const WhyJoinUsContent: React.FC<WhyJoinUsContentProps> = ({
  variant = 'full',
  showCTA = false,
  onCTAClick,
  onSecondaryCTAClick
}) => {
  const isCompact = variant === 'compact';

  return (
    <div className={`why-join-us ${isCompact ? 'why-join-us--compact' : ''}`}>
      {/* Background geometric shapes */}
      <div className="why-join-us__shapes">
        <div className="why-join-us__shape why-join-us__shape--1" />
        <div className="why-join-us__shape why-join-us__shape--2" />
        <div className="why-join-us__shape why-join-us__shape--3" />
      </div>

      {/* Section 1: Hero - Accroche */}
      <section className="why-join-us__section why-join-us__hero">
        <div className="why-join-us__monkey why-join-us__monkey--top">
          <LogoSVG variant="singeSuspenduVert" size="lg" />
        </div>
        <h1 className="why-join-us__title">
          Tourists are 500m away. They can't find your event.
        </h1>
        <p className="why-join-us__tagline">
          You post on Instagram. They're not following you.
        </p>
        <p className="why-join-us__tagline-sub">
          Drop your schedule — we put you on their map. 5 free events.
        </p>
      </section>

      {/* Section 2: Benefits - 3 key advantages */}
      {!isCompact && (
        <section className="why-join-us__section why-join-us__benefits">
          <div className="why-join-us__benefits-grid">
            <div className="why-join-us__benefit">
              <div className="why-join-us__benefit-header">
                <span className="why-join-us__benefit-label">Easy</span>
                <h3 className="why-join-us__benefit-title">Drop your schedule, done.</h3>
              </div>
              <p className="why-join-us__benefit-text">
                Photo, PDF, Excel or Instagram link — your events are created in seconds. 5 free.
              </p>
            </div>
            <div className="why-join-us__benefit">
              <div className="why-join-us__benefit-header">
                <span className="why-join-us__benefit-label">Local</span>
                <h3 className="why-join-us__benefit-title">Show up when they search nearby</h3>
              </div>
              <p className="why-join-us__benefit-text">
                QR codes in hotels, shops & cafes bring tourists straight to your event.
              </p>
            </div>
            <div className="why-join-us__benefit">
              <div className="why-join-us__benefit-header">
                <span className="why-join-us__benefit-label">Transparent</span>
                <h3 className="why-join-us__benefit-title">See who's watching</h3>
              </div>
              <p className="why-join-us__benefit-text">
                Real-time visibility on your events — real numbers, zero commission.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Section 3: What is What 2 Do ? - Le Concept */}
      {!isCompact && (
        <section className="why-join-us__section why-join-us__concept">
          <div className="why-join-us__concept-header">
            <span className="why-join-us__label">The platform</span>
            <h2 className="why-join-us__section-title">
              How tourists find you
            </h2>
            <p className="why-join-us__concept-subtitle">
              QR codes at hotels, shops & restaurants — one scan, your events appear
            </p>
          </div>
          <div className="why-join-us__concept-screens">
            <div className="why-join-us__screen-stack">
              <div className="why-join-us__screen why-join-us__screen--map">
                <img
                  src="/images/event_toursist_vue_map.webp"
                  alt="Vue carte touriste"
                  loading="lazy"
                />
                <span className="why-join-us__screen-badge">
                  <Icon name="Map" size={14} />
                  Map view
                </span>
              </div>
              <div className="why-join-us__screen why-join-us__screen--list">
                <img
                  src="/images/event_touriste_vue_list.webp"
                  alt="Vue liste swipe"
                  loading="lazy"
                />
                <span className="why-join-us__screen-badge">
                  <Icon name="Layers" size={14} />
                  Swipe view
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Section 3: How it works - Comment ca marche */}
      {!isCompact && (
        <section className="why-join-us__section why-join-us__how-it-works">
          <div className="why-join-us__how-header">
            <span className="why-join-us__label">Simple</span>
            <h2 className="why-join-us__section-title">
              Import your week in 30 seconds
            </h2>
            <p className="why-join-us__how-subtitle">
              Drop a photo, PDF or Instagram link — we create your events automatically. Recurring? Detected and grouped for you.
            </p>
          </div>
          <div className="why-join-us__how-screens why-join-us__how-screens--single">
            <div className="why-join-us__screen why-join-us__screen--creation">
              <img
                src="/images/screen_modal_desktop_create_event.webp"
                alt="Creation d'evenement"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      )}

      {/* Section 4: KPIs - Track performance */}
      {!isCompact && (
        <section className="why-join-us__section why-join-us__kpis">
          <div className="why-join-us__kpis-header">
            <span className="why-join-us__label">Analytics</span>
            <h2 className="why-join-us__section-title">
              Your events in numbers
            </h2>
            <p className="why-join-us__kpis-subtitle">
              Real-time stats per event — no guessing
            </p>
          </div>
          <div className="why-join-us__kpis-grid">
            <div className="why-join-us__kpi-card">
              <div className="why-join-us__kpi-icon why-join-us__kpi-icon--blue">
                <Icon name="Users" size={20} />
              </div>
              <span className="why-join-us__kpi-label">Tourists nearby</span>
              <span className="why-join-us__kpi-desc">Who's around your venue</span>
            </div>
            <div className="why-join-us__kpi-card">
              <div className="why-join-us__kpi-icon why-join-us__kpi-icon--purple">
                <Icon name="Eye" size={20} />
              </div>
              <span className="why-join-us__kpi-label">Event views</span>
              <span className="why-join-us__kpi-desc">How often your events are seen</span>
            </div>
            <div className="why-join-us__kpi-card">
              <div className="why-join-us__kpi-icon why-join-us__kpi-icon--green">
                <Icon name="BarChart3" size={20} />
              </div>
              <span className="why-join-us__kpi-label">Per event</span>
              <span className="why-join-us__kpi-desc">Average views per event</span>
            </div>
          </div>
        </section>
      )}

      {/* Section 5: Before/After */}
      {!isCompact && (
        <div className="why-join-us__comparison">
          <div className="why-join-us__comparison-row why-join-us__comparison-row--before">
            <div className="why-join-us__comparison-icon">
              <Icon name="X" size={16} />
            </div>
            <p className="why-join-us__comparison-text">
              2h/week creating events one by one. WhatsApp, Instagram, flyers. Zero data.
            </p>
          </div>
          <div className="why-join-us__comparison-divider" />
          <div className="why-join-us__comparison-row why-join-us__comparison-row--after">
            <div className="why-join-us__comparison-icon">
              <Icon name="Check" size={16} />
            </div>
            <p className="why-join-us__comparison-text">
              Drop your schedule, 30 seconds, done. See who discovers you — in real time.
            </p>
          </div>
        </div>
      )}

      {/* CTA Buttons */}
      {showCTA && (
        <div className="why-join-us__cta">
          <Button variant="primary" size="lg" onClick={onCTAClick} className="why-join-us__cta-primary">
            Create my first event
          </Button>
          <p className="why-join-us__cta-reassurance">
            Import or create — takes 30 seconds. No credit card.
          </p>
          {onSecondaryCTAClick && (
            <Button variant="secondary" size="lg" onClick={onSecondaryCTAClick} className="why-join-us__cta-secondary">
              Explore our app
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default WhyJoinUsContent;
